import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import axios from '../utils/api';
import { buildMediaImageDescriptor } from '../utils/mediaUrl';
import { useWishlist } from '../context/WishlistContext';
import { buildProductImageUrl } from '../utils/productImageUrl';
import { contrastText } from '../utils/color';
import ResponsiveImage from './ResponsiveImage';
import './navbar.css';

const getCategoryId = (category) => {
  if (!category) return '';
  return category._id || category.id || category;
};

const getParentId = (category) => {
  if (!category || !category.parent) return '';
  return typeof category.parent === 'object' ? getCategoryId(category.parent) : category.parent;
};

const normalizeHexColor = (value) => {
  const color = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (/^#[0-9a-f]{3}$/i.test(color)) {
    return `#${color.slice(1).split('').map(character => character.repeat(2)).join('')}`;
  }
  return '#000000';
};

const getColorChannels = (color) => [1, 3, 5]
  .map(index => parseInt(color.slice(index, index + 2), 16))
  .join(', ');

export default function NavBar() {
  const location = useLocation();
  const { cart, removeFromCart } = useCart();
  const totalQty = cart.reduce((s, i) => s + i.qty, 0);
  const { user, logout } = useAuth();
  const { count: wishlistCount } = useWishlist();
  const { brandName, brandLogo, showBrandLogoInNavbar, showBrandNameInNavbar, formatPrice, primaryColor } = useSettings();
  const navbarPrimaryColor = normalizeHexColor(primaryColor);
  const navbarAccentColor = contrastText(navbarPrimaryColor) === '#000000'
    ? '#000000'
    : navbarPrimaryColor;
  const navbarThemeStyle = {
    '--navbar-primary': navbarAccentColor,
    '--navbar-primary-rgb': getColorChannels(navbarAccentColor),
    '--navbar-primary-contrast': contrastText(navbarAccentColor)
  };
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [categorySearchSuggestions, setCategorySearchSuggestions] = useState([]);
  const [searchSuggestionsLoading, setSearchSuggestionsLoading] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSidebarAccountMenu, setShowSidebarAccountMenu] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [cartOffcanvas, setCartOffcanvas] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 992);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const categoryRailRef = useRef(null);
  const [categoryScroll, setCategoryScroll] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false
  });

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setSidebarOpen(false);
    setShowSidebarAccountMenu(false);
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchSuggestions([]);
      setCategorySearchSuggestions([]);
      setSearchSuggestionsLoading(false);
      setShowSearchSuggestions(false);
      setShowSearchForm(false);
    }
  };

  const handleSuggestionClick = () => {
    setSearchQuery('');
    setSearchSuggestions([]);
    setCategorySearchSuggestions([]);
    setSearchSuggestionsLoading(false);
    setShowSearchSuggestions(false);
    setShowSearchForm(false);
  };

  const handleCloseSearch = () => {
    setShowSearchForm(false);
    setShowSearchSuggestions(false);
  };

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth >= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const rail = categoryRailRef.current;
    if (!rail || !isLargeScreen) return undefined;

    const updateCategoryScroll = () => {
      const maxScrollLeft = Math.max(rail.scrollWidth - rail.clientWidth, 0);
      setCategoryScroll({
        hasOverflow: maxScrollLeft > 2,
        canScrollLeft: rail.scrollLeft > 2,
        canScrollRight: rail.scrollLeft < maxScrollLeft - 2
      });
    };

    const frameId = window.requestAnimationFrame(updateCategoryScroll);
    rail.addEventListener('scroll', updateCategoryScroll, { passive: true });
    window.addEventListener('resize', updateCategoryScroll);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateCategoryScroll)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(rail);
      const track = rail.querySelector('.navbar-category-track');
      if (track) resizeObserver.observe(track);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      rail.removeEventListener('scroll', updateCategoryScroll);
      window.removeEventListener('resize', updateCategoryScroll);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [categories, isLargeScreen]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchSuggestions([]);
      setCategorySearchSuggestions([]);
      setSearchSuggestionsLoading(false);
      setShowSearchSuggestions(false);
      return undefined;
    }

    const normalizedQuery = query.toLocaleLowerCase();
    setSearchSuggestions([]);
    setSearchSuggestionsLoading(true);
    setShowSearchSuggestions(true);
    setCategorySearchSuggestions(
      allCategories
        .filter(category => category.name?.toLocaleLowerCase().includes(normalizedQuery))
        .slice(0, 5)
    );

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const res = await axios.get('/api/products', {
          params: { search: query, active: true, limit: 8 },
          signal: controller.signal
        });
        const products = Array.isArray(res.data) ? res.data : (res.data.products || []);
        const titleMatches = products.filter(product =>
          product.name?.toLocaleLowerCase().includes(normalizedQuery)
        );
        setSearchSuggestions(titleMatches);
        setSearchSuggestionsLoading(false);
        setShowSearchSuggestions(true);
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') {
          setSearchSuggestions([]);
          setSearchSuggestionsLoading(false);
          setShowSearchSuggestions(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery, allCategories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchCategories = async () => {
      try {
        const res = await axios.get('/api/categories', { signal: controller.signal });
        const allCategories = Array.isArray(res.data) ? res.data : [];
        const subs = allCategories.reduce((acc, category) => {
          const parentId = getParentId(category);
          if (!parentId) return acc;
          if (!acc[parentId]) acc[parentId] = [];
          acc[parentId].push(category);
          return acc;
        }, {});

        setAllCategories(allCategories);
        setCategories(allCategories.filter(category => category.showInNavbar));
        setSubcategories(subs);
      } catch (err) {
        if (err.code !== 'ERR_CANCELED') console.log(err);
      }
    };
    fetchCategories();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!sidebarOpen && !cartOffcanvas) return undefined;

    const root = document.documentElement;
    const body = document.body;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPaddingRight = body.style.paddingRight;
    const previousOverscrollBehavior = body.style.overscrollBehavior;
    const scrollbarWidth = Math.max(window.innerWidth - root.clientWidth, 0);
    const currentPaddingRight = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    if (scrollbarWidth > 0) body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;

    return () => {
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.paddingRight = previousBodyPaddingRight;
      body.style.overscrollBehavior = previousOverscrollBehavior;
    };
  }, [sidebarOpen, cartOffcanvas]);

  useEffect(() => {
    if (!sidebarOpen && !showUserMenu && !cartOffcanvas && !showSearchForm) return undefined;
    const closeOnEscape = event => {
      if (event.key !== 'Escape') return;
      setSidebarOpen(false);
      setShowSidebarAccountMenu(false);
      setShowUserMenu(false);
      setCartOffcanvas(false);
      handleCloseSearch();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [sidebarOpen, showUserMenu, cartOffcanvas, showSearchForm]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.position-relative')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const scrollCategories = (direction) => {
    const rail = categoryRailRef.current;
    if (!rail) return;
    setHoveredCategory(null);
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.7, 260),
      behavior: 'smooth'
    });
  };

  const renderDesktopSubcategoryItem = (sub, depth = 0) => {
    const nestedSubs = subcategories[sub._id] || [];
    const desktopGroupClassName = [
      'navbar-subcategory-group',
      depth === 0 && (nestedSubs.length > 0
        ? 'navbar-subcategory-group--expanded'
        : 'navbar-subcategory-group--leaf')
    ].filter(Boolean).join(' ');

    return (
      <div key={sub._id} className={desktopGroupClassName}>
        <Link
          className="dropdown-item navbar-subcategory-link"
          to={`/products?category=${sub.slug}`}
          onClick={() => setHoveredCategory(null)}
        >
          <span>{sub.name}</span>
        </Link>
        {nestedSubs.length > 0 && renderDesktopSubcategories(nestedSubs, depth + 1)}
      </div>
    );
  };

  const renderDesktopSubcategories = (items, depth = 0) => {
    if (depth > 0) {
      return (
        <div className="navbar-subcategory-list">
          {items.map(sub => renderDesktopSubcategoryItem(sub, depth))}
        </div>
      );
    }

    const expandedItems = items.filter(sub => (subcategories[sub._id] || []).length > 0);
    const leafItems = items.filter(sub => (subcategories[sub._id] || []).length === 0);

    return (
      <div className="navbar-subcategory-sections">
        {expandedItems.length > 0 && (
          <section className="navbar-subcategory-section" aria-label="Categories with subcategories">
            <div className="navbar-subcategory-grid navbar-subcategory-grid--expanded">
              {expandedItems.map(sub => renderDesktopSubcategoryItem(sub))}
            </div>
          </section>
        )}
        {leafItems.length > 0 && (
          <section className="navbar-subcategory-section" aria-label="More categories">
            {expandedItems.length > 0 && <h3 className="navbar-subcategory-section-title">More categories</h3>}
            <div className="navbar-subcategory-grid navbar-subcategory-grid--leaf">
              {leafItems.map(sub => renderDesktopSubcategoryItem(sub))}
            </div>
          </section>
        )}
      </div>
    );
  };

  const renderMobileSubcategories = (items, depth = 1) => (
    items.map(sub => {
      const nestedSubs = subcategories[sub._id] || [];
      return (
        <React.Fragment key={sub._id}>
          <Link
            className="nav-link d-block sidebar-subcategory-link"
            to={`/products?category=${sub.slug}`}
            onClick={() => setSidebarOpen(false)}
            style={{ '--sidebar-depth': depth }}
          >
            {sub.name}
          </Link>
          {nestedSubs.length > 0 && renderMobileSubcategories(nestedSubs, depth + 1)}
        </React.Fragment>
      );
    })
  );

  const renderSearchSuggestions = () => {
    if (!showSearchSuggestions) return null;

    const hasSuggestions =
      searchSuggestions.length > 0 || categorySearchSuggestions.length > 0;

    if (!hasSuggestions && searchSuggestionsLoading) return null;

    return (
      <div className="navbar-search-suggestions position-absolute start-0 end-0 bg-white border shadow-sm">
        {!hasSuggestions && (
          <div className="px-3 py-3 text-center text-muted">
            No product or category found
          </div>
        )}
        {categorySearchSuggestions.map(category => (
          <Link
            key={`category-${category._id}`}
            className="navbar-search-suggestion d-flex align-items-center px-3 py-2 text-dark text-decoration-none"
            to={`/products?category=${category.slug || category._id}`}
            onClick={handleSuggestionClick}
          >
            <i className="fa-solid fa-folder me-2 text-muted" aria-hidden="true"></i>
            <span>{category.name}</span>
            <small className="text-muted ms-auto ps-2">Category</small>
          </Link>
        ))}
        {searchSuggestions.map(product => (
          <Link
            key={`product-${product._id}`}
            className="navbar-search-suggestion d-flex align-items-center px-3 py-2 text-dark text-decoration-none"
            to={`/products/${product.slug || product._id}`}
            onClick={handleSuggestionClick}
          >
            <i className="fa-solid fa-tag me-2 text-muted" aria-hidden="true"></i>
            <span>{product.name}</span>
            <small className="text-muted ms-auto ps-2">Product</small>
          </Link>
        ))}
      </div>
    );
  };

  const activeDesktopCategory = categories.find(
    category => category._id === hoveredCategory && (subcategories[category._id] || []).length > 0
  );
  const activeCategoryParam = new URLSearchParams(location.search).get('category');
  const isCategoryActive = category => location.pathname === '/products'
    && [category._id, category.slug].map(String).includes(String(activeCategoryParam));

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 767px) {
            .navbar-categories {
              overflow-x: auto;
            }
          }
          
          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            width: 250px;
            height: 100vh;
            height: 100dvh;
            max-height: 100vh;
            max-height: 100dvh;
            background: white;
            z-index: 1000;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            display: flex;
            flex-direction: column;
          }
          .sidebar.open {
            transform: translateX(0);
          }
          .sidebar-menu-header {
            flex: 0 0 auto;
          }
          .sidebar-menu-body {
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
          }
          .overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.5);
            z-index: 999;
          }
          
          /* Category Dropdown Animations */
          .dropdown {
            position: relative;
          }
          .dropdown-menu {
            opacity: 0;
            visibility: hidden;
            transform: translateY(-8px) scale(0.95);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .dropdown-menu.show {
            opacity: 1;
            visibility: visible;
            transform: translateY(0) scale(1);
          }
          
          /* Mobile Sidebar Menu Items */
          .sidebar .nav-link {
            padding: 0.45rem 0 !important;
            font-size: 0.92rem !important;
            font-weight: 500;
            letter-spacing: 0.3px;
          }
          
          .sidebar .nav-link.d-block {
            padding-top: 0.45rem !important;
            padding-bottom: 0.45rem !important;
          }

          .sidebar .btn.nav-link {
            padding: 0.25rem 0.35rem !important;
            font-size: 0.75rem !important;
          }

          .sidebar-header-account {
            display: grid;
            width: 34px;
            height: 34px;
            place-items: center;
            padding: 0;
            color: #202020;
            font-size: 1rem;
            text-decoration: none;
            border: 0;
            border-radius: 50%;
            background: transparent;
            cursor: pointer;
          }

          .sidebar-header-account:hover,
          .sidebar-header-account:focus-visible {
            color: var(--navbar-primary);
            background: rgba(var(--navbar-primary-rgb), 0.07);
          }

          .sidebar-account-panel {
            flex: 0 0 auto;
            background: #fafafa;
          }

          .sidebar-account-panel .nav-link,
          .sidebar-account-panel .btn.nav-link {
            padding: 0.4rem 0 !important;
            color: #202020;
            font-size: 0.88rem !important;
            text-decoration: none;
          }
          
          body {
            padding-top: 52px;
          }

          .navbar.fixed-top {
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
          }

          .navbar a,
          .navbar a:hover,
          .navbar a:focus,
          .navbar a:active {
            text-decoration: none !important;
          }

          .navbar-top-row {
            min-height: 44px;
          }

          .navbar-category-navigation {
            position: relative;
          }

          .navbar-category-rail-shell {
            display: flex;
            align-items: stretch;
            min-width: 0;
          }

          .navbar-category-rail {
            flex: 1 1 auto;
            min-width: 0;
            overflow-x: auto;
            overflow-y: hidden;
            overscroll-behavior-x: contain;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          .navbar-category-rail::-webkit-scrollbar {
            display: none;
          }

          .navbar-category-track {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 2.4rem;
            min-width: 100%;
            width: max-content;
            padding: 0.3rem 0.25rem;
            white-space: nowrap;
          }

          .navbar-category-scroll-button {
            position: relative;
            z-index: 2;
            flex: 0 0 38px;
            width: 38px;
            display: grid;
            place-items: center;
            border: 0;
            background: #fff;
            color: #202020;
            transition: color 0.15s ease, opacity 0.15s ease;
          }

          .navbar-category-scroll-button:hover:not(:disabled),
          .navbar-category-scroll-button:focus-visible {
            color: var(--navbar-primary);
          }

          .navbar-category-scroll-button:focus-visible {
            outline: 2px solid rgba(var(--navbar-primary-rgb), 0.25);
            outline-offset: -3px;
          }

          .navbar-category-scroll-button:disabled {
            color: #aaa;
            cursor: default;
            opacity: 0.45;
          }

          .navbar-category-item {
            flex: 0 0 auto;
            text-align: center;
          }

          .navbar-category-item > .nav-link,
          .navbar-category-link {
            white-space: nowrap;
            font-weight: 700;
            line-height: 1.25;
            transition: color 0.2s ease;
          }

          .navbar-category-link:hover,
          .navbar-category-link:focus,
          .navbar-category-item:hover > .navbar-category-link {
            color: var(--navbar-primary) !important;
          }

          .navbar-category-chevron {
            transition: transform 0.25s ease, color 0.2s ease;
          }

          .navbar-category-item:hover .navbar-category-chevron {
            transform: rotate(180deg);
            color: var(--navbar-primary);
          }

          .navbar-category-link.is-active {
            color: var(--navbar-primary) !important;
          }

          .navbar-category-item,
          .navbar-category-link {
            position: relative;
          }

          .navbar-category-menu {
            display: block;
            position: absolute;
            top: 100%;
            left: 50%;
            z-index: 1035;
            width: min(1080px, calc(100vw - 2rem));
            max-height: min(600px, calc(100vh - 130px));
            margin: 0;
            padding: 0;
            overflow: hidden;
            border: 1px solid #eee;
            border-top: 3px solid var(--navbar-primary);
            border-radius: 0 0 0.75rem 0.75rem;
            background: #fff;
            box-shadow: 0 18px 45px rgba(40, 22, 45, 0.16) !important;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transform: translate(-50%, 12px);
            transform-origin: top center;
            transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease;
          }

          .navbar-category-menu.show {
            pointer-events: auto;
            opacity: 1;
            visibility: visible;
            transform: translate(-50%, 0);
          }

          .navbar-category-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1.5rem;
            padding: 1.15rem 1.5rem;
            border-bottom: 1px solid #ececec;
            background: linear-gradient(110deg, rgba(var(--navbar-primary-rgb), 0.055) 0%, #fff 58%, rgba(var(--navbar-primary-rgb), 0.035) 100%);
          }

          .navbar-category-menu-eyebrow {
            display: block;
            margin-bottom: 0.15rem;
            color: var(--navbar-primary);
            font-size: 0.7rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .navbar-category-menu-title {
            margin: 0;
            color: #241b27;
            font-size: 1.2rem;
            font-weight: 750;
            line-height: 1.25;
          }

          .navbar-category-view-all {
            display: inline-flex;
            flex: 0 0 auto;
            align-items: center;
            gap: 0.5rem;
            padding: 0.55rem 0.85rem;
            color: var(--navbar-primary);
            font-size: 0.82rem;
            font-weight: 700;
            text-decoration: none;
            border: 1px solid rgba(var(--navbar-primary-rgb), 0.24);
            border-radius: 999px;
            background: #fff;
            transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
          }

          .navbar-category-view-all:hover,
          .navbar-category-view-all:focus-visible {
            color: var(--navbar-primary-contrast);
            border-color: var(--navbar-primary);
            background: var(--navbar-primary);
            transform: translateY(-1px);
          }

          .navbar-category-menu-body {
            max-height: min(470px, calc(100vh - 235px));
            padding: 1.35rem 1.5rem 1.5rem;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-color: rgba(var(--navbar-primary-rgb), 0.35) transparent;
            scrollbar-width: thin;
          }

          .navbar-category-menu-body::-webkit-scrollbar {
            width: 7px;
          }

          .navbar-category-menu-body::-webkit-scrollbar-thumb {
            border-radius: 999px;
            background: rgba(var(--navbar-primary-rgb), 0.35);
          }

          .navbar-subcategory-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            align-items: start;
            gap: 0.9rem;
          }

          .navbar-subcategory-sections {
            display: grid;
            gap: 1.2rem;
          }

          .navbar-subcategory-section-title {
            display: flex;
            align-items: center;
            gap: 0.65rem;
            margin: 0 0 0.7rem;
            color: var(--navbar-primary);
            font-size: 0.72rem;
            font-weight: 750;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .navbar-subcategory-section-title::after {
            content: '';
            flex: 1 1 auto;
            height: 1px;
            background: rgba(var(--navbar-primary-rgb), 0.15);
          }

          .navbar-subcategory-grid--leaf {
            gap: 0.65rem 0.75rem;
          }

          .navbar-subcategory-grid > .navbar-subcategory-group {
            align-self: start;
            min-width: 0;
            border: 1px solid #eee9ef;
            border-radius: 0.6rem;
            background: #fff;
            transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
          }

          .navbar-subcategory-group--expanded {
            padding: 0.85rem 0.9rem;
          }

          .navbar-subcategory-group--leaf {
            padding: 0;
          }

          .navbar-subcategory-grid > .navbar-subcategory-group:hover {
            border-color: rgba(var(--navbar-primary-rgb), 0.25);
            box-shadow: 0 8px 22px rgba(46, 27, 51, 0.08);
            transform: translateY(-2px);
          }

          .navbar-subcategory-grid > .navbar-subcategory-group > .navbar-subcategory-link {
            display: flex;
            align-items: center;
            color: #211d22;
            font-size: 0.96rem;
            font-weight: 750;
            border-radius: 0;
          }

          .navbar-subcategory-grid > .navbar-subcategory-group--expanded > .navbar-subcategory-link {
            padding: 0 0 0.55rem;
            border-bottom: 1px solid #f0ebf1;
          }

          .navbar-subcategory-grid > .navbar-subcategory-group--leaf > .navbar-subcategory-link {
            min-height: 46px;
            padding: 0.55rem 0.75rem;
            border-radius: 0.55rem;
          }

          .navbar-subcategory-list {
            display: grid;
            gap: 0.08rem;
            margin: 0.45rem 0 0;
          }

          .navbar-subcategory-link {
            display: block;
            padding: 0.32rem 0.4rem;
            line-height: 1.35;
            white-space: normal;
            overflow-wrap: anywhere;
            border-radius: 0.25rem;
          }

          .navbar-subcategory-link:hover,
          .navbar-subcategory-link:focus {
            color: var(--navbar-primary);
            background: rgba(var(--navbar-primary-rgb), 0.07);
          }

          .navbar-subcategory-grid > .navbar-subcategory-group > .navbar-subcategory-link:hover,
          .navbar-subcategory-grid > .navbar-subcategory-group > .navbar-subcategory-link:focus {
            color: var(--navbar-primary);
            background: transparent;
          }

          .navbar-subcategory-list .navbar-subcategory-link {
            color: #3d373f;
            font-size: 0.92rem;
            font-weight: 500;
          }

          .navbar-subcategory-list .navbar-subcategory-list .navbar-subcategory-link {
            padding-left: 0.85rem;
            color: #4b454d;
            font-size: 0.88rem;
            font-weight: 500;
          }

          @media (max-width: 1199px) {
            .navbar-category-track {
              gap: 1.8rem;
            }

            .navbar-category-menu {
              width: calc(100vw - 2rem);
            }

            .navbar-subcategory-grid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
          }

          @media (min-width: 992px) {
            body {
              padding-top: 98px;
            }

            .navbar-top-row {
              min-height: 44px;
            }
          }

          .navbar-brand-logo {
            max-width: 150px;
            max-height: 38px;
            object-fit: contain;
          }

          @media (max-width: 991px) {
            body {
              padding-top: 45px;
            }

            .navbar.fixed-top {
              min-height: 44px;
              padding-top: 0;
              padding-bottom: 0;
            }

            .navbar-brand-logo {
              max-width: 112px;
              max-height: 30px;
            }

            .navbar-actions {
              gap: 0.25rem !important;
              padding-right: 0.5rem;
            }

            .navbar-actions .nav-link,
            .navbar-menu-button {
              display: grid;
              width: 28px;
              height: 36px;
              place-items: center;
            }

            .navbar-actions .badge {
              top: 4px !important;
              left: 100% !important;
              transform: translate(-50%, 0) !important;
            }

            .mobile-navbar-search {
              top: 45px !important;
            }
          }

          @media (max-width: 991px) {
            .navbar-top-row {
              position: relative;
              min-height: 44px;
            }

            .navbar-brand-wrap {
              position: static;
            }

            .navbar-brand-link {
              position: absolute;
              left: 50%;
              top: 50%;
              transform: translate(-50%, -50%);
              justify-content: center;
              max-width: calc(100vw - 150px);
              overflow: hidden;
              z-index: 1;
            }

            .navbar-brand-link b {
              overflow: hidden;
              min-width: 0;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .navbar-menu-button,
            .navbar-actions {
              position: relative;
              z-index: 2;
            }
          }

          .navbar-cart-offcanvas {
            --bs-offcanvas-width: min(360px, calc(100vw - 48px));
            width: var(--bs-offcanvas-width);
            max-width: calc(100vw - 48px);
          }

          .navbar-search-wrap {
            min-width: 0;
          }

          .navbar-search-input {
            border-color: #000;
            padding-right: 2.5rem;
          }

          .navbar-search-input:focus {
            border-color: #000;
            box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.2);
          }

          .navbar-search-input::-webkit-search-cancel-button {
            -webkit-appearance: none;
            appearance: none;
          }

          .navbar-search-close {
            z-index: 2;
            color: #000;
          }

          .navbar-search-suggestions {
            top: calc(100% + 0.25rem);
            z-index: 1050;
            max-height: 320px;
            overflow-y: auto;
          }

          .navbar-search-suggestion {
            line-height: 1.35;
          }

          .navbar-search-suggestion:hover,
          .navbar-search-suggestion:focus {
            background: #f3f3f3;
          }

          @media (max-width: 360px) {
            .navbar-cart-offcanvas {
              --bs-offcanvas-width: calc(100vw - 32px);
              max-width: calc(100vw - 32px);
            }
          }
        `
      }} />
      <nav className="navbar navbar-expand-lg navbar-light bg-white fixed-top storefront-navbar" style={navbarThemeStyle}>
        <div className="w-100">
          {/* Upper part: Branding, Search, Cart, User */}
          <div className="container">
            <div className="navbar-top-row d-flex justify-content-between align-items-center">
              <div className="navbar-brand-wrap d-flex align-items-center">
                <button className="navbar-menu-button navbar-action-button btn btn-link text-dark d-lg-none p-0" type="button" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(true); }} aria-label="Open navigation menu">
                  <i className="fa fa-bars" aria-hidden="true"></i>
                </button>
                <Link className="navbar-brand navbar-brand-link d-flex align-items-center gap-2 m-0" to="/">
                  {showBrandLogoInNavbar && brandLogo && (
                    <ResponsiveImage className="navbar-brand-logo" source={buildMediaImageDescriptor(brandLogo, '140px')} alt={`${brandName} logo`} loading="eager" />
                  )}
                  {showBrandNameInNavbar && <b>{brandName}</b>}
                </Link>
              </div>
              <div className="navbar-actions d-flex align-items-center gap-2">
                {!showSearchForm && (
                  <button className="btn btn-link nav-link p-0 navbar-action-button" onClick={() => setShowSearchForm(true)} aria-label="Open search">
                    <i className="fa-solid fa-search" aria-hidden="true"></i>
                  </button>
                )}
                {isLargeScreen && showSearchForm && (
                  <div className="navbar-search-wrap navbar-search-wrap--desktop position-relative" ref={searchRef}>
                    <form className="d-flex" onSubmit={handleSearch}>
                      <div className="position-relative flex-grow-1">
                        <input
                          className="form-control navbar-search-input"
                          type="search"
                          placeholder="Search products..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onFocus={() => searchQuery.trim() && setShowSearchSuggestions(true)}
                          aria-label="Search products"
                          autoFocus
                          autoComplete="off"
                        />
                        <button
                          className="navbar-search-close btn btn-link position-absolute top-50 end-0 translate-middle-y px-3 py-0 text-decoration-none"
                          type="button"
                          onClick={handleCloseSearch}
                          aria-label="Close search"
                        >
                          <i className="fa-solid fa-times" aria-hidden="true"></i>
                        </button>
                      </div>
                      <button className="navbar-search-submit" type="submit" aria-label="Submit product search">
                        <i className="fa-solid fa-search" aria-hidden="true"></i>
                      </button>
                    </form>
                    {renderSearchSuggestions()}
                  </div>
                )}

                <Link className="btn btn-link nav-link position-relative p-0 navbar-action-button" to="/wishlist" aria-label="Wishlist">
                  <i className="fa-regular fa-heart" aria-hidden="true"></i>
                  {wishlistCount > 0 && (
                    <span className="navbar-action-count" aria-hidden="true">
                      {wishlistCount}
                    </span>
                  )}
                </Link>

                <button className="btn btn-link nav-link position-relative p-0 navbar-action-button" type="button" onClick={() => setCartOffcanvas(true)} aria-label={`Open cart, ${totalQty} ${totalQty === 1 ? 'item' : 'items'}`}>
                  <i className="fa fa-cart-shopping" aria-hidden="true"></i>
                  {totalQty > 0 && (
                      <span className="navbar-action-count" aria-hidden="true">
                      {totalQty}
                    </span>
                  )}
                </button>

                <div className="position-relative navbar-account-menu d-none d-lg-block">
                  <button
                      className="btn btn-link nav-link p-0 navbar-action-button"
                      type="button"
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      aria-label={showUserMenu ? 'Close account menu' : 'Open account menu'}
                      aria-expanded={showUserMenu}
                  >
                    <i className="fa-regular fa-user" aria-hidden="true"></i>
                  </button>

                  {showUserMenu && (
                      <div className="dropdown-menu show position-absolute end-0 mt-2 navbar-account-dropdown">
                      {user ? (
                        <>
                          <h6 className="dropdown-header">{user.name || user.email}</h6>
                          <hr className="dropdown-divider m-0" />
                          <Link className="dropdown-item" to="/profile" onClick={() => setShowUserMenu(false)}>
                            <i className="fa fa-user me-2"></i>Profile
                          </Link>
                          {user.isAdmin && (
                            <>
                              <button className="dropdown-item" onClick={() => { navigate('/admin'); setShowUserMenu(false); }}>
                                <i className="fa fa-gauge me-2"></i>Admin Dashboard
                              </button>
                              <hr className="dropdown-divider m-0" />
                            </>
                          )}
                          <button className="dropdown-item" onClick={handleLogout}>
                            <i className="fa-solid fa-right-from-bracket me-2"></i>Logout
                          </button>
                        </>
                      ) : (
                        <>
                          <Link className="dropdown-item" to="/login" onClick={() => setShowUserMenu(false)}>
                            <i className="fa-solid fa-right-to-bracket me-2"></i>Login
                          </Link>
                          <Link className="dropdown-item" to="/register" onClick={() => setShowUserMenu(false)}>
                            <i className="fa-solid fa-user-plus me-2"></i>Register
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="border-top d-none d-lg-block"></div>
          {/* Lower part: Categories */}
          <div
            className="container d-none d-lg-block px-3 navbar-category-navigation"
            onMouseLeave={() => setHoveredCategory(null)}
          >
            <div className="navbar-category-rail-shell">
              {categoryScroll.hasOverflow && (
                <button
                  type="button"
                  className="navbar-category-scroll-button navbar-category-scroll-button--left"
                  onClick={() => scrollCategories(-1)}
                  disabled={!categoryScroll.canScrollLeft}
                  aria-label="Scroll categories left"
                >
                  <i className="fa fa-chevron-left" aria-hidden="true"></i>
                </button>
              )}

              <div className="navbar-category-rail" ref={categoryRailRef}>
                <div className="navbar-category-track">
                  <Link
                    className={`nav-link navbar-category-item navbar-category-link ${location.pathname === '/' ? 'is-current' : ''}`}
                    to="/"
                    onMouseEnter={() => setHoveredCategory(null)}
                    style={{ textTransform: 'uppercase', fontSize: '0.9rem' }}
                  >
                    HOME
                  </Link>

                  {categories.map(cat => {
                    const subs = subcategories[cat._id] || [];
                    if (subs.length > 0) {
                      return (
                        <div
                          key={cat._id}
                          className="navbar-category-item"
                          onMouseEnter={() => setHoveredCategory(cat._id)}
                          onFocus={() => setHoveredCategory(cat._id)}
                        >
                          <Link
                            className={`nav-link navbar-category-link ${hoveredCategory === cat._id ? 'is-active' : ''} ${isCategoryActive(cat) ? 'is-current' : ''}`}
                            to={`/products?category=${cat.slug}`}
                            onClick={() => setHoveredCategory(null)}
                            aria-haspopup="true"
                            aria-expanded={hoveredCategory === cat._id}
                            style={{ textTransform: 'uppercase', fontSize: '0.9rem' }}
                          >
                            {cat.name} <i className="fa fa-chevron-down ms-1 navbar-category-chevron" style={{ fontSize: '0.7rem' }}></i>
                          </Link>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={cat._id}
                        className={`nav-link navbar-category-item navbar-category-link ${isCategoryActive(cat) ? 'is-current' : ''}`}
                        to={`/products?category=${cat.slug}`}
                        onMouseEnter={() => setHoveredCategory(null)}
                        style={{ textTransform: 'uppercase', fontSize: '0.9rem' }}
                      >
                        {cat.name}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {categoryScroll.hasOverflow && (
                <button
                  type="button"
                  className="navbar-category-scroll-button navbar-category-scroll-button--right"
                  onClick={() => scrollCategories(1)}
                  disabled={!categoryScroll.canScrollRight}
                  aria-label="Scroll categories right"
                >
                  <i className="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
              )}
            </div>

            <div
              className={`dropdown-menu navbar-category-menu shadow ${activeDesktopCategory ? 'show' : ''}`}
              aria-label={activeDesktopCategory ? `${activeDesktopCategory.name} subcategories` : undefined}
            >
              {activeDesktopCategory && (
                <>
                  <div className="navbar-category-menu-header">
                    <div>
                      <span className="navbar-category-menu-eyebrow">Explore category</span>
                      <h2 className="navbar-category-menu-title">{activeDesktopCategory.name}</h2>
                    </div>
                    <Link
                      className="navbar-category-view-all"
                      to={`/products?category=${activeDesktopCategory.slug}`}
                      onClick={() => setHoveredCategory(null)}
                    >
                      Shop all {activeDesktopCategory.name}
                    </Link>
                  </div>
                  <div className="navbar-category-menu-body">
                    {renderDesktopSubcategories(subcategories[activeDesktopCategory._id] || [])}
                  </div>
                </>
              )}
            </div>
          </div>
          {sidebarOpen && <div className="overlay navbar-overlay" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }}></div>}
          <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} aria-hidden={!sidebarOpen} aria-label="Mobile navigation">
            <div className="sidebar-menu-header">
              <Link className="sidebar-brand" to="/" onClick={() => setSidebarOpen(false)}>
                {showBrandLogoInNavbar && brandLogo ? (
                  <ResponsiveImage className="sidebar-brand-logo" source={buildMediaImageDescriptor(brandLogo, '96px')} alt="" loading="eager" />
                ) : <span className="sidebar-brand-mark"><i className="fa-solid fa-shopping-bag" aria-hidden="true" /></span>}
                <span><small>Welcome to</small><strong>{brandName}</strong></span>
              </Link>
              <div className="sidebar-header-actions">
              <button
                type="button"
                className="sidebar-header-account"
                onClick={() => setShowSidebarAccountMenu(current => !current)}
                aria-label={showSidebarAccountMenu ? 'Close account menu' : 'Open account menu'}
                aria-expanded={showSidebarAccountMenu}
                title={user ? 'Account' : 'Login'}
              >
                <i className="fa-regular fa-user" aria-hidden="true"></i>
              </button>
                <button className="sidebar-close-button" type="button" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }} aria-label="Close navigation menu">
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            {showSidebarAccountMenu && <div className="sidebar-account-panel">
              {user ? (
                <>
                  <div className="small text-muted text-truncate mb-1">{user.name || user.email}</div>
                  <Link className="nav-link d-block" to="/profile" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }}>
                    <i className="fa-regular fa-user me-2" aria-hidden="true"></i>Profile
                  </Link>
                  {user.isAdmin && <Link className="nav-link d-block" to="/admin" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }}>
                    <i className="fa fa-gauge me-2" aria-hidden="true"></i>Admin Dashboard
                  </Link>}
                  <button type="button" className="btn btn-link nav-link d-block w-100 text-start" onClick={handleLogout}>
                    <i className="fa-solid fa-right-from-bracket me-2" aria-hidden="true"></i>Logout
                  </button>
                </>
              ) : (
                <>
                  <Link className="nav-link d-block" to="/login" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }}>
                    <i className="fa-solid fa-right-to-bracket me-2" aria-hidden="true"></i>Login
                  </Link>
                  <Link className="nav-link d-block" to="/register" onClick={() => { setShowSidebarAccountMenu(false); setSidebarOpen(false); }}>
                    <i className="fa-solid fa-user-plus me-2" aria-hidden="true"></i>Create Account
                  </Link>
                </>
              )}
            </div>}
            <div className="sidebar-menu-body">
              <span className="sidebar-menu-eyebrow">Browse categories</span>
              <Link className={`nav-link d-block sidebar-nav-link ${location.pathname === '/' ? 'is-current' : ''}`} to="/" onClick={() => setSidebarOpen(false)}><i className="fa-solid fa-home" aria-hidden="true" />Home</Link>
              {categories.map(cat => {
                const subs = subcategories[cat._id] || [];
                const isExpanded = expandedCategories.has(cat._id);
                if (subs.length === 0) {
                  return (
                    <Link
                      key={cat._id}
                      className={`nav-link d-block sidebar-nav-link ${isCategoryActive(cat) ? 'is-current' : ''}`}
                      to={`/products?category=${cat.slug}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {cat.name}
                    </Link>
                  );
                }

                return (
                  <div key={cat._id} className={`sidebar-category-group ${isExpanded ? 'is-expanded' : ''}`}>
                    <button
                      className={`sidebar-category-toggle ${isCategoryActive(cat) ? 'is-current' : ''}`}
                      type="button"
                      aria-expanded={isExpanded}
                      aria-controls={`sidebar-category-${cat._id}`}
                         onClick={() => {
                           const newExpanded = new Set(expandedCategories);
                           if (isExpanded) {
                             newExpanded.delete(cat._id);
                           } else {
                             newExpanded.add(cat._id);
                           }
                           setExpandedCategories(newExpanded);
                         }}
                    >
                      <span>
                        {cat.name}
                      </span>
                      {subs.length > 0 && (
                        <span className="sidebar-category-chevron" aria-hidden="true">
                          <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} aria-hidden="true"></i>
                        </span>
                      )}
                    </button>
                    {isExpanded && <div className="sidebar-subcategory-panel" id={`sidebar-category-${cat._id}`}>
                      <Link className="sidebar-shop-all" to={`/products?category=${cat.slug}`} onClick={() => setSidebarOpen(false)}>Shop all {cat.name}</Link>
                      {renderMobileSubcategories(subs)}
                    </div>}
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      </nav>

      {!isLargeScreen && showSearchForm && (
        <div className="fixed-top mobile-navbar-search bg-white border-bottom p-3" style={{ ...navbarThemeStyle, zIndex: 1040 }}>
          <div className="container">
            <div className="navbar-search-wrap position-relative" ref={searchRef} style={{ maxWidth: '400px', margin: '0 auto' }}>
              <form className="d-flex justify-content-center" onSubmit={handleSearch}>
                <div className="position-relative flex-grow-1">
                  <input
                    className="form-control navbar-search-input"
                    type="search"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim() && setShowSearchSuggestions(true)}
                    aria-label="Search products"
                    autoFocus
                    autoComplete="off"
                  />
                  <button
                    className="navbar-search-close btn btn-link position-absolute top-50 end-0 translate-middle-y px-3 py-0 text-decoration-none"
                    type="button"
                    onClick={handleCloseSearch}
                    aria-label="Close search"
                  >
                    <i className="fa-solid fa-times" aria-hidden="true"></i>
                  </button>
                </div>
                <button className="btn btn-outline-secondary ms-2" type="submit" aria-label="Submit product search">
                  <i className="fa-solid fa-search" aria-hidden="true"></i>
                </button>
              </form>
              {renderSearchSuggestions()}
            </div>
          </div>
        </div>
      )}

      {/* Cart Offcanvas */}
      <div className={`offcanvas offcanvas-end navbar-cart-offcanvas ${cartOffcanvas ? 'show' : ''}`} style={{ ...navbarThemeStyle, visibility: cartOffcanvas ? 'visible' : 'hidden' }}>
        <div className="offcanvas-header navbar-cart-header">
          <div><span>Your selection</span><h2 className="offcanvas-title">Shopping cart</h2></div>
          <button type="button" className="btn-close" onClick={() => setCartOffcanvas(false)} aria-label="Close cart"></button>
        </div>
        <div className="offcanvas-body">
          {cart.length === 0 ? (
            <div className="navbar-cart-empty"><span><i className="fa-solid fa-shopping-bag" aria-hidden="true" /></span><h3>Your cart is empty</h3><p>Discover something you’ll love.</p><Link to="/products" onClick={() => setCartOffcanvas(false)}>Start shopping</Link></div>
          ) : (
            <>
              <div className="navbar-cart-items">
                {cart.map((item, index) => (
                  <div key={`${item.product || index}-${item.variationId || ''}`} className="navbar-cart-item">
                    {(() => {
                      const src = buildProductImageUrl(item.image, 'small');
                      return <ResponsiveImage src={src} sizes="72px" width={72} height={72} alt="" className="navbar-cart-item-image" />;
                    })()}
                    <div className="navbar-cart-item-copy">
                      <Link to={`/products/${item.product}`} className="text-black text-decoration-none" onClick={() => setCartOffcanvas(false)}>
                        <strong>{item.name}</strong>
                      </Link>
                      {item.size && <p className="mb-1 small text-muted">Size: {item.size}</p>}
                      <p className="mb-0">{formatPrice(item.price)} <span>× {item.qty}</span></p>
                    </div>
                    <button
                      type="button"
                      className="navbar-cart-remove"
                      onClick={() => removeFromCart(item.product, item.variationId)}
                      title="Remove from cart"
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <i className="fa fa-times" aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
              </div>
              <div className="navbar-cart-actions">
                <Link to="/checkout" className="navbar-cart-checkout" onClick={() => setCartOffcanvas(false)}>Checkout <i className="fa-solid fa-arrow-right" aria-hidden="true" /></Link>
                <Link to="/cart" className="navbar-cart-details" onClick={() => setCartOffcanvas(false)}>View cart details</Link>
              </div>
            </>
          )}
        </div>
      </div>
      {cartOffcanvas && <div className="offcanvas-backdrop show" onClick={() => setCartOffcanvas(false)}></div>}
    </>
  );
}
